import React, { useEffect, useState } from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { Button, Field, Flex, Input, Message, Modal, Text } from '@workwhile/ui'
import { useIndeedFlexContextValue } from '../../context/IndeedFlexProvider'

export const linkIndeedFlexLocationFormSchema = z.object({
  companyLocationId: z.coerce
    .number({ invalid_type_error: 'Company location ID is required' })
    .int('Company location ID must be an integer')
    .positive('Company location ID must be positive'),
})

export type LinkIndeedFlexLocationForm = z.infer<
  typeof linkIndeedFlexLocationFormSchema
>

type LinkIndeedFlexLocationButtonProps = {
  ifClientId: number
  ifVenueId: number
  venueName: string
  clientName: string
}

export const LinkIndeedFlexLocationButton = ({
  ifClientId,
  ifVenueId,
  venueName,
  clientName,
}: LinkIndeedFlexLocationButtonProps) => {
  const {
    actions: { linkIndeedFlexLocation, resetLinkIndeedFlexLocation },
    state: { linkIndeedFlexLocationMutationState },
  } = useIndeedFlexContextValue()

  const [isOpen, setIsOpen] = useState(false)

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<LinkIndeedFlexLocationForm>({
    resolver: zodResolver(linkIndeedFlexLocationFormSchema),
    defaultValues: {
      companyLocationId: undefined,
    },
  })

  useEffect(() => {
    if (!linkIndeedFlexLocationMutationState.isSuccess) {
      return
    }

    setIsOpen(false)
    reset()
    resetLinkIndeedFlexLocation()
  }, [
    linkIndeedFlexLocationMutationState.isSuccess,
    reset,
    resetLinkIndeedFlexLocation,
  ])

  const handleClick = () => {
    resetLinkIndeedFlexLocation()
    reset()
    setIsOpen(true)
  }

  const handleCancel = () => {
    resetLinkIndeedFlexLocation()
    reset()
    setIsOpen(false)
  }

  const onSubmit = handleSubmit((data) => {
    linkIndeedFlexLocation({
      ifClientId,
      ifVenueId,
      companyLocationId: data.companyLocationId,
    })
  })

  return (
    <>
      <Button
        variant="secondary"
        kind="small"
        onClick={handleClick}
        style={{ padding: '0 0.5rem' }}
        disabled={linkIndeedFlexLocationMutationState.isPending}
        loading={linkIndeedFlexLocationMutationState.isPending}
      >
        <Text fontSize="12px">Link location</Text>
      </Button>
      {isOpen ? (
        <Modal
          open
          onClose={handleCancel}
          title={`Link location for ${venueName}`}
        >
          <Text mb={2}>
            Enter the WorkWhile company location ID to link {venueName} (
            {ifVenueId}) for client {clientName} ({ifClientId}).
          </Text>
          {linkIndeedFlexLocationMutationState.isError &&
          linkIndeedFlexLocationMutationState.errorMessage ? (
            <Message variant="error" mb={3}>
              {linkIndeedFlexLocationMutationState.errorMessage}
            </Message>
          ) : null}
          <form onSubmit={onSubmit}>
            <Field
              label="Company location ID"
              error={errors.companyLocationId?.message}
            >
              <Input type="number" {...register('companyLocationId')} />
            </Field>
            <Flex flexDirection="row" justifyContent="space-between" gap={2}>
              <Button variant="secondary" onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="submit"
                loading={linkIndeedFlexLocationMutationState.isPending}
                disabled={linkIndeedFlexLocationMutationState.isPending}
              >
                Submit
              </Button>
            </Flex>
          </form>
        </Modal>
      ) : null}
    </>
  )
}
